import React, { useEffect, useRef, useState } from 'react'
import {
  Checkbox,
  Button,
  Divider,
  Typography,
  InputAdornment,
  Icon,
  FormControlLabel,
} from '@material-ui/core'
import { TextFieldFormsy } from '@fuse'
import Formsy from 'formsy-react'
import { Link } from 'react-router-dom'
import * as authActions from 'app/auth/store/actions'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import ErrorBoundary from 'app/components/ErrorBoundary'
import { emailRegExpFormat } from 'commons/email'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { Routes } from 'app/services/routeService/routeService'
import AuthenticatedCardHeader from 'app/components/AuthenticateCardHeader'
import TextLinkToDialogButton from 'app/components/buttons/TextLinkToDialogButton'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import { useAppName } from 'commons/usePartner'
import { openNewTab, termsOfUseUrl, privacyPolicyUrl } from 'commons/website'
import InputPasswordFormsy from 'app/componentV4/Input/InputPasswordFormsy'
import { useFlag } from '@unleash/proxy-client-react'

const useStyles = makeStyles((theme) => ({
  Or: {
    color: theme.palette.primary.subtitle,
  },
  Link: {
    color: theme.palette.secondary.main,
    marginLeft: 8,
  },
}))

const AdapterLink = React.forwardRef((props, ref) => (
  <Link innerRef={ref} {...props} />
))

export default (props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const login = useSelector(({ auth }) => auth.login)
  const mes_1960_new_signin_signup = useFlag('mes_1960_new_signin_signup')

  const appName = useAppName()
  const [isFormValid, setIsFormValid] = useState(false)

  const [isKeepMeSignedIn, setIskeepMeSignedIn] = useState(false)

  const formRef = useRef(null)
  const { formatMessage } = useIntl()

  useEffect(() => {
    if (login.error && (login.error.email || login.error.password)) {
      formRef.current.updateInputsWithError({
        ...login.error,
      })
      disableButton()
    }
  }, [login.error])

  function disableButton() {
    setIsFormValid(false)
  }

  function enableButton() {
    setIsFormValid(true)
  }

  function handleSubmit({ email, password }) {
    dispatch(
      authActions.submitLogin(
        {
          username: email.trim(),
          password,
          isKeepMeSignedIn,
        },
        formatMessage,
      ),
    )
  }

  return (
    <div className="w-full sm:py-36 sm:px-64 py-24 px-16">
      <AuthenticatedCardHeader
        title={formatMessage(
          { id: 'login.title' },
          {
            appName,
            b: (text) => (
              <span className="font-bold" key={text}>
                {text}
              </span>
            ),
          },
        )}
        subtitle={formatMessage({ id: 'login.introduction' })}
      />

      <div className="flex flex-col items-center w-full">
        <ErrorBoundary
          fallback={
            <div className="flex flex-col p-8 rounded shadow-lg">
              <span className="font-bold">
                <FormattedMessage id="error.general" />
              </span>
            </div>
          }
        >
          <Formsy
            onValidSubmit={handleSubmit}
            onValid={enableButton}
            onInvalid={disableButton}
            ref={formRef}
            className="flex flex-col justify-center w-full xs:w-xs md:w-2/3 lg:w-1/3"
          >
            <TextFieldFormsy
              className="mb-16"
              type="email"
              name="email"
              label={formatMessage({
                id: 'email.address.label',
              })}
              validations={{
                matchRegexp: emailRegExpFormat,
              }}
              validationErrors={{
                matchRegexp: formatMessage({
                  id: 'login.textField.validation.email',
                }),
              }}
              inputProps={{
                'data-testid': 'email',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon className="text-20" color="action">
                      email
                    </Icon>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              autoFocus
              required
            />

            <InputPasswordFormsy
              className="mb-16"
              type="password"
              name="password"
              label={formatMessage({
                id: 'login.textField.placeHolder.password',
              })}
              value=""
              validations={{
                minLength: 6,
              }}
              validationErrors={{
                minLength: formatMessage({
                  id: 'login.textField.validation.minLengthPassword',
                }),
              }}
              inputProps={{
                'data-testid': 'password',
              }}
              variant="outlined"
              required
            />
            <PrimaryButton
              type="submit"
              variant="contained"
              color="secondary"
              className="w-full mx-auto mt-16 uppercase"
              aria-label="LOG IN"
              disabled={!isFormValid || login.isSubmitting}
              isLoading={login.isSubmitting}
              value="legacy"
              CircularProp={{ color: 'primary' }}
              data-testid="submit"
              labelId="login.signin"
            />
          </Formsy>
        </ErrorBoundary>

        <div className="flex flex-row my-8 mt-12 justify-between flex-wrap  w-full xs:w-xs md:w-2/3 lg:w-1/3">
          <div className="flex items-center">
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(event) => {
                    setIskeepMeSignedIn(event.target.checked)
                  }}
                  inputProps={{
                    'data-testid': 'keepMeSignedIn',
                  }}
                />
              }
              label={
                <Typography>
                  <FormattedMessage id="login.keepMeSignedIn" />
                </Typography>
              }
            />
          </div>
          <div className="flex">
            <Button
              className="normal-case"
              color="secondary"
              component={AdapterLink}
              to={
                mes_1960_new_signin_signup
                  ? Routes.forgotPassword()
                  : Routes.forgot()
              }
              data-testid="forgotPassword"
            >
              <FormattedMessage id="login.forgotPasswordTwoPart" />
            </Button>
          </div>
        </div>

        <div className="w-xs sm:w-1/3 flex flex-row items-center">
          <Divider
            className={clsx(classes.secondaryDivider, 'flex-grow')}
            color="secondary"
          />
          <Typography
            variant="subtitle1"
            color="secondary"
            className={clsx(classes.Or, 'h5 mx-16 uppercase')}
          >
            <FormattedMessage id="registerForm.or" />
          </Typography>
          <Divider className={clsx(classes.secondaryDivider, 'flex-grow')} />
        </div>

        <div className="flex flex-row flex-wrap w-xs lg:w-1/3 text-center mt-8">
          <Typography className="flex-1 flex-shrink align-middle self-center">
            <Button
              className="normal-case"
              color="secondary"
              component={AdapterLink}
              to={
                mes_1960_new_signin_signup ? Routes.signIn() : Routes.register()
              }
              data-testid="signUp"
            >
              <FormattedMessage id="login.createAccountTwoPart" />
            </Button>
          </Typography>
        </div>

        <div className="flex text-center">
          <Typography className="mt-20">
            <FormattedMessage
              id="login.acceptTermAndPolicySentence"
              values={{
                termOfService: (
                  <TextLinkToDialogButton
                    text={<FormattedMessage id="login.termsOfService" />}
                    onClick={() => openNewTab(termsOfUseUrl)}
                    data-testid="termOfService"
                  />
                ),
                privacyPolicy: (
                  <TextLinkToDialogButton
                    text={<FormattedMessage id="login.privacyPolicy" />}
                    onClick={() => openNewTab(privacyPolicyUrl)}
                    data-testid="privacyPolicy"
                  />
                ),
              }}
            />
          </Typography>
        </div>
      </div>
    </div>
  )
}
